import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import { Link } from 'react-router-dom';
import { authApi } from '../../services/api';

// Password policy checks mirror `backend/utils/validation.py::validate_password_strength`.
// Keep both in sync when the policy changes.
const passwordChecks = (pwd) => ({
  minLength: (pwd || '').length >= 8,
  uppercase: /[A-Z]/.test(pwd || ''),
  lowercase: /[a-z]/.test(pwd || ''),
  number: /\d/.test(pwd || ''),
});

const PolicyRule = ({ ok, label, testid }) => (
  <li className="flex items-center gap-2" data-testid={testid}>
    {ok ? (
      <Check className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
    ) : (
      <X className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
    )}
    <span className={ok ? 'text-green-700' : 'text-gray-500'}>{label}</span>
  </li>
);

// After signing in: back to the page the visitor was sent away from (same-site paths only), else reload.
const finishAuth = () => {
  let next = null;
  try { next = sessionStorage.getItem('postLoginRedirect'); sessionStorage.removeItem('postLoginRedirect'); } catch { /* private mode */ }
  if (next && next.startsWith('/') && !next.startsWith('//')) window.location.assign(next);
  else window.location.reload();
};

const AuthModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [isLoading, setIsLoading] = useState(false);

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  // Register form state
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptedTerms: false,
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await authApi.login({
        email: loginData.email,
        password: loginData.password,
        remember_me: loginData.rememberMe  // Send remember me preference
      });
      
      
      // Save tokens to localStorage (always use localStorage for persistence)
      localStorage.setItem('accessToken', response.access_token);
      localStorage.setItem('refreshToken', response.refresh_token);
      
      // Store user info
      if (response.user) {
        localStorage.setItem('userRole', response.user.role);
        localStorage.setItem('userEmail', response.user.email);
      }
      
      toast({
        title: t('common.success'),
        description: t('auth.loginSuccess'),
      });
      
      onClose();
      
      // Redirect based on role
      if (response.user && response.user.role === 'admin') {
        window.location.href = '/admin'; // Use window.location.href for hard redirect
      } else {
        finishAuth();
      }
    } catch (error) {
      // Handle error message properly
      let errorMsg = t('auth.loginFailed');
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMsg = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast({
        title: t('common.error'),
        description: errorMsg,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.acceptedTerms) {
      toast({
        title: t('common.error'),
        description: t('auth.mustAcceptTerms'),
        variant: 'destructive',
      });
      return;
    }
    
    if (registerData.password !== registerData.confirmPassword) {
      toast({
        title: t('common.error'),
        description: t('auth.passwordMismatch'),
        variant: 'destructive',
      });
      return;
    }

    // Client-side policy gate — mirrors backend rules so the user gets
    // instant feedback instead of a round-trip rejection.
    const checks = passwordChecks(registerData.password);
    if (!Object.values(checks).every(Boolean)) {
      toast({
        title: t('common.error'),
        description: t('auth.passwordPolicyTitle'),
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await authApi.register({
        email: registerData.email,
        password: registerData.password,
        firstName: registerData.firstName,
        lastName: registerData.lastName,
        acceptedTerms: registerData.acceptedTerms === true
      });
      
      // Save tokens to localStorage
      localStorage.setItem('accessToken', response.access_token);
      localStorage.setItem('refreshToken', response.refresh_token);
      
      toast({
        title: t('common.success'),
        description: t('auth.registerSuccess'),
      });
      
      onClose();
      finishAuth();
    } catch (error) {
      // Handle error message properly
      let errorMsg = t('auth.registerFailed');
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMsg = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast({
        title: t('common.error'),
        description: errorMsg,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex justify-center">
            <img 
              src="/logo-cimonamia.png" 
              alt={t('authModalMore.logoAlt')} 
              className="h-10 w-auto"
            />
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">{t('auth.login')}</TabsTrigger>
            <TabsTrigger value="register">{t('auth.signup')}</TabsTrigger>
          </TabsList>

          {/* Login Tab */}
          <TabsContent value="login">
            <div className="space-y-4 py-4">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold mb-1">{t('auth.loginHeading')}</h3>
                <p className="text-sm text-gray-500">
                  {t('auth.loginSubheading')}
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-4" style={{ pointerEvents: 'auto' }}>
                <div className="space-y-2">
                  <Label htmlFor="login-email">{t('auth.email')}</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder={t('authModalMore.emailPlaceholder')}
                    value={loginData.email}
                    onChange={(e) =>
                      setLoginData({ ...loginData, email: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password">{t('auth.password')}</Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={loginData.password}
                    onChange={(e) =>
                      setLoginData({ ...loginData, password: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="remember"
                      checked={loginData.rememberMe}
                      onCheckedChange={(checked) =>
                        setLoginData({ ...loginData, rememberMe: checked })
                      }
                    />
                    <label
                      htmlFor="remember"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {t('auth.rememberMe')}
                    </label>
                  </div>
                  <Link
                    to="/forgot-password"
                    className="text-sm hover:underline"
                    style={{ color: 'var(--ink)' }}
                    onClick={onClose}
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>

                <Button
                  type="submit"
                  className="w-full text-white transition-colors"
                  style={{
                    background: 'var(--ink)',
                    border: 'none',
                    padding: '14px',
                    fontFamily: 'var(--font-body)',
                    fontSize: '11px',
                    letterSpacing: '3px',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--terracotta)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--ink)')}
                  disabled={isLoading}
                >
                  {isLoading ? t('common.loading') : t('auth.logIn')}
                </Button>
              </form>

              <div className="text-center text-sm">
                <span className="text-gray-500">{t('auth.newUser')} </span>
                <button
                  onClick={() => setActiveTab('register')}
                  className="hover:underline font-medium"
                  style={{ color: 'var(--ink)' }}
                >
                  {t('auth.signup')}
                </button>
              </div>

              <div className="text-center">
              </div>
            </div>
          </TabsContent>

          {/* Register Tab */}
          <TabsContent value="register">
            <div className="space-y-4 py-4">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold mb-1">{t('auth.signupHeading')}</h3>
                <p className="text-sm text-gray-500">
                  {t('auth.signupSubheading')}{' '}
                  <Link to="/privacy" className="hover:underline" style={{ color: 'var(--ink)' }} onClick={onClose}>
                    {t('auth.privacyPolicy')}
                  </Link>
                  .
                </p>
              </div>

              <form onSubmit={handleRegister} className="space-y-4" style={{ pointerEvents: 'auto' }}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">{t('auth.firstName')}</Label>
                    <Input
                      id="firstName"
                      type="text"
                      value={registerData.firstName}
                      onChange={(e) =>
                        setRegisterData({ ...registerData, firstName: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">{t('auth.lastName')}</Label>
                    <Input
                      id="lastName"
                      type="text"
                      value={registerData.lastName}
                      onChange={(e) =>
                        setRegisterData({ ...registerData, lastName: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-email">{t('auth.email')}</Label>
                  <Input
                    id="register-email"
                    type="email"
                    placeholder={t('authModalMore.emailPlaceholder')}
                    value={registerData.email}
                    onChange={(e) =>
                      setRegisterData({ ...registerData, email: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-password">{t('auth.password')}</Label>
                  <Input
                    id="register-password"
                    data-testid="register-password-input"
                    type="password"
                    value={registerData.password}
                    onChange={(e) =>
                      setRegisterData({ ...registerData, password: e.target.value })
                    }
                    required
                  />
                  {/* Live password policy checklist — mirrors backend validate_password_strength */}
                  {(() => {
                    const checks = passwordChecks(registerData.password);
                    const allOk = Object.values(checks).every(Boolean);
                    return (
                      <div
                        className="mt-1 rounded-md border border-gray-200 bg-gray-50 p-2.5"
                        data-testid="password-policy-checklist"
                        role="status"
                        aria-live="polite"
                      >
                        <p className={`text-xs font-medium mb-1.5 ${allOk ? 'text-green-700' : 'text-gray-600'}`}>
                          {t('auth.passwordPolicyTitle')}
                        </p>
                        <ul className="text-xs space-y-1">
                          <PolicyRule ok={checks.minLength} label={t('auth.passwordPolicyMinLength')} testid="policy-min-length" />
                          <PolicyRule ok={checks.uppercase} label={t('auth.passwordPolicyUppercase')} testid="policy-uppercase" />
                          <PolicyRule ok={checks.lowercase} label={t('auth.passwordPolicyLowercase')} testid="policy-lowercase" />
                          <PolicyRule ok={checks.number} label={t('auth.passwordPolicyNumber')} testid="policy-number" />
                        </ul>
                      </div>
                    );
                  })()}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">{t('auth.confirmPassword')}</Label>
                  <Input
                    id="confirm-password"
                    data-testid="register-confirm-password-input"
                    type="password"
                    value={registerData.confirmPassword}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        confirmPassword: e.target.value,
                      })
                    }
                    required
                  />
                  {registerData.confirmPassword &&
                    registerData.password !== registerData.confirmPassword && (
                      <p className="text-xs text-red-600" data-testid="password-mismatch-hint">
                        {t('auth.passwordMismatch')}
                      </p>
                    )}
                </div>

                <div className="flex items-start space-x-2 pt-1">
                  <Checkbox
                    id="accept-terms"
                    data-testid="signup-accept-terms-checkbox"
                    checked={registerData.acceptedTerms}
                    onCheckedChange={(checked) =>
                      setRegisterData({ ...registerData, acceptedTerms: checked === true })
                    }
                    className="mt-0.5"
                  />
                  <label
                    htmlFor="accept-terms"
                    className="text-sm leading-snug text-gray-700 select-none"
                  >
                    {t('auth.acceptTerms')}{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline font-medium"
                  style={{ color: 'var(--ink)' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('auth.termsAndConditions')}
                    </Link>{' '}
                    {t('auth.and')}{' '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline font-medium"
                  style={{ color: 'var(--ink)' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('auth.privacyPolicy')}
                    </Link>
                    .
                  </label>
                </div>

                <Button
                  type="submit"
                  data-testid="signup-submit-button"
                  className="w-full text-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  style={{
                    background: 'var(--ink)',
                    border: 'none',
                    padding: '14px',
                    fontFamily: 'var(--font-body)',
                    fontSize: '11px',
                    letterSpacing: '3px',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  }}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled) e.currentTarget.style.background = 'var(--terracotta)';
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--ink)')}
                  disabled={isLoading || !registerData.acceptedTerms}
                >
                  {isLoading ? t('common.loading') : t('auth.register')}
                </Button>
              </form>

              <div className="text-center text-sm">
                <span className="text-gray-500">{t('auth.existingUser')} </span>
                <button
                  onClick={() => setActiveTab('login')}
                  className="hover:underline font-medium"
                  style={{ color: 'var(--ink)' }}
                >
                  {t('auth.logIn')}
                </button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;